import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import './ManagerTeamPage.css';

const ManagerTeamPage = () => {
  const navigate = useNavigate();
  const [manager, setManager] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  useEffect(() => {
    const fetchData = () => {
      try {
        const savedManager = sessionStorage.getItem('viewingManager');
        const savedTeam = sessionStorage.getItem('viewingTeamMembers');
        
        if (savedManager && savedTeam) {
          setManager(JSON.parse(savedManager));
          setTeamMembers(JSON.parse(savedTeam));
        } else {
          // If no data in sessionStorage, redirect back
          navigate('/dashboard?tab=managers');
        }
      } catch (error) {
        console.error('Error loading manager team:', error);
        navigate('/dashboard?tab=managers');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getUniqueDepartments = () => {
    return [...new Set(teamMembers.map(member => member.department))];
  };

  const filteredAndSortedMembers = () => {
    let filtered = teamMembers.filter(member => {
      const matchesSearch = 
        member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.emp_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.role?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = filterStatus === 'all' || member.status === filterStatus;
      const matchesDepartment = filterDepartment === 'all' || member.department === filterDepartment;
      
      return matchesSearch && matchesStatus && matchesDepartment;
    });

    // Sorting
    filtered.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  const exportToExcel = () => {
    const exportData = filteredAndSortedMembers().map(member => ({
      'Employee ID': member.emp_id,
      'Name': member.name,
      'Email': member.email,
      'Role': member.role,
      'Department': member.department,
      'Status': member.status,
      'Phone': member.phone || 'N/A',
      'Date of Joining': member.dateOfJoining || 'N/A',
      'Reporting Manager': member.reporting_manager || 'N/A'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Team Members');
    
    const fileName = `${manager?.name.replace(/\s+/g, '_')}_Team_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const goBack = () => {
    navigate('/dashboard?tab=managers');
  };

  const viewEmployeeProfile = (employeeId) => {
    navigate(`/employee-profile/${employeeId}`);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading team details...</p>
      </div>
    );
  }

  if (!manager) {
    return (
      <div className="error-container">
        <h2>No Manager Data Found</h2>
        <button onClick={goBack} className="back-button">
          ← Back to Managers
        </button>
      </div>
    );
  }

  const filteredMembers = filteredAndSortedMembers();

  return (
    <div className="manager-team-page">
      {/* Header */}
      <div className="page-header">
        <button onClick={goBack} className="back-button">
          ← Back to Managers
        </button>
        <h1>Team Management</h1>
      </div>

      {/* Manager Info Card */}
      <div className="manager-info-card">
        <div className="manager-basic-info">
          <div className="manager-avatar-large">
            {manager.name?.charAt(0).toUpperCase()}
          </div>
          <div className="manager-details">
            <h2>{manager.name}</h2>
            <div className="manager-meta">
              <div className="meta-item">
                <span className="label">Department:</span>
                <span className="value">{manager.department}</span>
              </div>
              <div className="meta-item">
                <span className="label">Email:</span>
                <span className="value">{manager.email}</span>
              </div>
              <div className="meta-item">
                <span className="label">Phone:</span>
                <span className="value">{manager.phone || 'N/A'}</span>
              </div>
              <div className="meta-item">
                <span className="label">Employee ID:</span>
                <span className="value">{manager.emp_id || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="team-stats-cards">
          <div className="stat-card">
            <div className="stat-number">{teamMembers.length}</div>
            <div className="stat-label">Total Team</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              {teamMembers.filter(m => m.status === 'active').length}
            </div>
            <div className="stat-label">Active</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              {teamMembers.filter(m => m.status === 'inactive').length}
            </div>
            <div className="stat-label">Inactive</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              {getUniqueDepartments().length}
            </div>
            <div className="stat-label">Departments</div>
          </div>
        </div>
      </div>

      {/* Controls Section */}
      <div className="controls-section">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search team members..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>

        <div className="filters-container">
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <select 
            value={filterDepartment} 
            onChange={(e) => setFilterDepartment(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Departments</option>
            {getUniqueDepartments().map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>
        </div>

        <div className="actions-container">
          <button onClick={exportToExcel} className="action-btn export-btn">
            📥 Export Excel
          </button>
          <button className="action-btn print-btn" onClick={() => window.print()}>
            🖨️ Print
          </button>
        </div>
      </div>

      {/* Team Members Table */}
      <div className="team-table-container">
        <div className="table-header">
          <h3>Team Members ({filteredMembers.length} of {teamMembers.length})</h3>
        </div>
        
        <table className="team-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('emp_id')} className="sortable">
                Employee ID {sortConfig.key === 'emp_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('name')} className="sortable">
                Name {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('role')} className="sortable">
                Role {sortConfig.key === 'role' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('department')} className="sortable">
                Department {sortConfig.key === 'department' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th>Email</th>
              <th>Phone</th>
              <th onClick={() => handleSort('status')} className="sortable">
                Status {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMembers.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">
                  <div className="empty-state">
                    <div className="empty-icon">👥</div>
                    <p>No team members found matching your criteria</p>
                    <button 
                      onClick={() => {
                        setSearchTerm('');
                        setFilterStatus('all');
                        setFilterDepartment('all');
                      }}
                      className="reset-filters-btn"
                    >
                      Reset Filters
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              filteredMembers.map(member => (
                <tr key={member._id}>
                  <td>
                    <span className="employee-id">{member.emp_id}</span>
                  </td>
                  <td>
                    <div className="employee-cell">
                      <div className="avatar" onClick={() => viewEmployeeProfile(member._id)}>
                        {member.name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="employee-info">
                        <div className="name">{member.name}</div>
                        <div className="role">{member.role}</div>
                      </div>
                    </div>
                  </td>
                  <td>{member.role}</td>
                  <td>
                    <span className="department-badge">{member.department}</span>
                  </td>
                  <td>
                    <a href={`mailto:${member.email}`} className="email-link">
                      {member.email}
                    </a>
                  </td>
                  <td>{member.phone || 'N/A'}</td>
                  <td>
                    <span className={`status-badge status-${member.status}`}>
                      {member.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        onClick={() => viewEmployeeProfile(member._id)}
                        className="btn-view"
                        title="View Profile"
                      >
                        👁️ View
                      </button>
                      <button 
                        onClick={() => window.location.href = `mailto:${member.email}`}
                        className="btn-email"
                        title="Send Email"
                      >
                        ✉️ Email
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer Stats */}
      <div className="footer-stats">
        <div className="stat-summary">
          <div className="summary-item">
            <span className="label">Active Members:</span>
            <span className="value">{teamMembers.filter(m => m.status === 'active').length}</span>
          </div>
          <div className="summary-item">
            <span className="label">Inactive Members:</span>
            <span className="value">{teamMembers.filter(m => m.status === 'inactive').length}</span>
          </div>
          <div className="summary-item">
            <span className="label">Departments:</span>
            <span className="value">{getUniqueDepartments().length}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagerTeamPage;